/**
 * Unified Booking Page Component
 * Two-column layout with booking flow on left and patient info on right
 */

import { useState, useMemo, useEffect } from 'react';
import { format, addDays, addWeeks, startOfDay, startOfWeek, isSameDay, differenceInWeeks, parse, isValid } from 'date-fns';
import { useWidgetStore } from '@/store/widgetStore';
import { useAvailability } from '@/hooks';
import { CustomDatePicker } from '@/components/ui';
import type { Provider, AppointmentSlot } from '@/types';

// Icons
const PlusIcon = () => (
  <span className="ww-inline-flex ww-items-center ww-justify-center ww-w-5 ww-h-5 ww-rounded-full ww-border ww-border-current">
    <svg className="ww-w-3 ww-h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  </span>
);

const ChevronLeftIcon = () => (
  <svg className="ww-w-4 ww-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="ww-w-4 ww-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);


const UserIcon = () => (
  <svg className="ww-w-full ww-h-full ww-text-gray-400" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

interface PatientFormData {
  name: string;
  phone: string;
  email: string;
  dateOfBirth: string;
  reasonForVisit: string;
  insuranceProvider: string;
  memberId: string;
  groupMember: string;
}

interface BookingPageProps {
  onConfirm: (data: {
    provider: Provider;
    slot: AppointmentSlot;
    patientInfo: PatientFormData;
    service: string;
    location: string;
    paymentMethodId?: string;
  }) => void;
}

export const BookingPage = ({ onConfirm }: BookingPageProps) => {
  const { services, clinicians, locations, practice, paymentMethods } = useWidgetStore();

  // Availability check hook
  const {
    slots: availabilitySlots,
    loading: loadingSlots,
    error: availabilityError,
    checkSlots,
    clearSlots,
  } = useAvailability({
    practiceId: practice?.id || '',
  });

  // Use only clinicians from API (no hardcoded/legacy providers)
  const displayProviders: Provider[] = useMemo(() => {
    return clinicians.map((clinician) => ({
      id: clinician.id,
      name: clinician.name,
      title: '',
      specialty: clinician.specialty || '',
      bio: clinician.bio || '',
      avatarUrl: clinician.avatar_url,
      acceptingNewPatients: true,
    }));
  }, [clinicians]);

  // Selection state
  const [selectedService, setSelectedService] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [selectedSlot, setSelectedSlot] = useState<AppointmentSlot | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showInsurance, setShowInsurance] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  // Patient form state
  const [patientInfo, setPatientInfo] = useState<PatientFormData>({
    name: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    reasonForVisit: '',
    insuranceProvider: '',
    memberId: '',
    groupMember: '',
  });

  // Fetch availability when service, provider, location, and date are selected
  useEffect(() => {
    if (selectedService && selectedProvider && selectedLocation && selectedDate) {
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      checkSlots({
        serviceId: selectedService,
        clinicianId: selectedProvider.id,
        locationId: selectedLocation,
        date: dateString,
      });
    } else {
      clearSlots();
    }
  }, [selectedService, selectedProvider, selectedLocation, selectedDate, checkSlots, clearSlots]);

  // Generate week dates based on week offset
  const weekDates = useMemo(() => {
    const dates = [];
    const today = startOfDay(new Date());
    const weekStart = startOfWeek(addWeeks(today, weekOffset), { weekStartsOn: 1 }); // Monday as start
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(weekStart, i));
    }
    return dates;
  }, [weekOffset]);

  // Auto-adjust week view when selected date is outside current week
  useEffect(() => {
    const today = startOfDay(new Date());
    const currentWeekStart = startOfWeek(addWeeks(today, weekOffset), { weekStartsOn: 1 });
    const selectedWeekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const selectedWeekOffset = differenceInWeeks(selectedWeekStart, startOfWeek(today, { weekStartsOn: 1 }));
    
    // Only adjust if selected date is not in the current week view
    const isInCurrentWeek = selectedDate >= currentWeekStart && selectedDate < addDays(currentWeekStart, 7);
    if (!isInCurrentWeek && selectedWeekOffset !== weekOffset) {
      setWeekOffset(selectedWeekOffset);
    }
  }, [selectedDate, weekOffset]);

  // Convert AvailabilitySlot to AppointmentSlot format
  const convertedSlots: AppointmentSlot[] = useMemo(() => {
    return availabilitySlots.map((slot) => ({
      id: `${slot.location_id}-${slot.start_datetime}`,
      providerId: selectedProvider?.id || '',
      startTime: slot.start_datetime,
      endTime: slot.end_datetime,
      duration: 50, // Default duration
      available: true,
      appointmentType: 'Evaluation',
    }));
  }, [availabilitySlots, selectedProvider]);

  // Group slots by time period
  const groupedSlots = useMemo(() => {
    const morning: AppointmentSlot[] = [];
    const afternoon: AppointmentSlot[] = [];
    const evening: AppointmentSlot[] = [];

    convertedSlots.forEach((slot) => {
      const hour = new Date(slot.startTime).getHours();
      if (hour < 12) {
        morning.push(slot);
      } else if (hour < 15) {
        afternoon.push(slot);
      } else {
        evening.push(slot);
      }
    });

    return { morning, afternoon, evening };
  }, [convertedSlots]);

  // Handle form input changes
  const handleInputChange = (field: keyof PatientFormData, value: string) => {
    setPatientInfo((prev) => ({ ...prev, [field]: value }));
  };

  // Convert dateOfBirth string to Date | null
  const dateOfBirthDate = useMemo(() => {
    if (!patientInfo.dateOfBirth) return null;
    const parsed = parse(patientInfo.dateOfBirth, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  }, [patientInfo.dateOfBirth]);

  // Handle date of birth change from CustomDatePicker
  const handleDateOfBirthChange = (date: Date | null) => {
    const dateString = date ? format(date, 'yyyy-MM-dd') : '';
    handleInputChange('dateOfBirth', dateString);
  };

  // Handle review booking
  const handleReviewBooking = () => {
    if (!selectedProvider || !selectedSlot) return;

    onConfirm({
      provider: selectedProvider,
      slot: selectedSlot,
      patientInfo,
      service: selectedService,
      location: selectedLocation,
      paymentMethodId: selectedPaymentMethod || undefined,
    });
  };

  // Check if form is complete
  const isFormComplete =
    selectedProvider &&
    selectedSlot &&
    patientInfo.name &&
    patientInfo.phone &&
    patientInfo.email &&
    patientInfo.dateOfBirth;

  return (
    <div className="ww-grid ww-grid-cols-1 lg:ww-grid-cols-5 ww-gap-6">
      {/* Left Column - Booking Flow */}
      <div className="lg:ww-col-span-3 ww-space-y-6">
        {/* Services and Location */}
        <div className="ww-grid ww-grid-cols-2 ww-gap-4">
          <div>
            <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
              Services
            </label>
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="ww-w-full ww-px-3 ww-py-3.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm ww-bg-white focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
            >
              <option value="">Select appointment type</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} {service.duration && `(${service.duration} mins)`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
              Location
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="ww-w-full ww-px-3 ww-py-3.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm ww-bg-white focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
            >
              <option value="">Select location</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Provider Cards - only from API clinicians */}
        <div className="ww-grid ww-grid-cols-3 ww-gap-4">
          {displayProviders.length === 0 ? (
            <div className="ww-col-span-3 ww-py-8 ww-px-4 ww-text-center ww-text-gray-500 ww-border ww-border-dashed ww-border-gray-300 ww-rounded-lg ww-bg-gray-50/50">
              <p className="ww-font-medium">No clinicians available</p>
              <p className="ww-text-sm ww-mt-1">This practice has not yet added clinicians for online booking. Please contact the practice to schedule.</p>
            </div>
          ) : (
          displayProviders.map((provider) => (
            <div
              key={provider.id}
              className={`ww-border ww-rounded-lg ww-p-4 ww-text-center ww-cursor-pointer ww-transition-all ${
                selectedProvider?.id === provider.id
                  ? 'ww-border-primary ww-bg-primary/5'
                  : 'ww-border-gray-200 hover:ww-border-gray-300'
              }`}
              onClick={() => setSelectedProvider(provider)}
            >
              <div className="ww-w-16 ww-h-16 ww-rounded-full ww-mx-auto ww-mb-3 ww-overflow-hidden ww-bg-gray-100">
                {provider.avatarUrl ? (
                  <img
                    src={provider.avatarUrl}
                    alt={provider.name}
                    className="ww-w-full ww-h-full ww-object-cover"
                  />
                ) : (
                  <div className="ww-w-full ww-h-full ww-flex ww-items-center ww-justify-center">
                    <UserIcon />
                  </div>
                )}
              </div>
              <h3 className="ww-font-medium ww-text-sm ww-text-gray-900">
                {provider.name}
              </h3>
              <p className="ww-text-xs ww-text-gray-500 ww-mb-2">
                {provider.specialty}
              </p>
              <p className="ww-text-xs ww-text-gray-400 ww-mb-3 ww-line-clamp-2">
                {provider.bio}
              </p>
              <button
                className={`ww-inline-flex ww-items-center ww-gap-1 ww-px-3 ww-py-1.5 ww-text-xs ww-rounded ww-border ww-transition-colors ${
                  selectedProvider?.id === provider.id
                    ? 'ww-bg-gray-100 ww-text-gray-400 ww-border-gray-200'
                    : 'ww-border-gray-300 ww-text-gray-700 hover:ww-bg-gray-50'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProvider(provider);
                }}
              >
                <PlusIcon />
                Add
              </button>
            </div>
          ))
          )}
        </div>

        {/* Date Selector */}
        <div>
          <div className="ww-flex ww-items-center ww-justify-between ww-mb-4">
            <h3 className="ww-font-medium ww-text-gray-900">
              {isSameDay(selectedDate, new Date())
                ? `Today, ${format(selectedDate, 'MMMM d')}`
                : format(selectedDate, 'MMMM d')}
            </h3>
            <div className="ww-flex ww-gap-1">
              <button
                onClick={() => setWeekOffset((prev) => prev - 1)}
                className="ww-p-1 ww-rounded ww-shadow-sm ww-border ww-border-gray-200 ww-bg-white hover:ww-bg-gray-50"
                aria-label="Previous week"
              >
                <ChevronLeftIcon />
              </button>
              <button
                onClick={() => setWeekOffset((prev) => prev + 1)}
                className="ww-p-1 ww-rounded ww-shadow-sm ww-border ww-border-gray-200 ww-bg-white hover:ww-bg-gray-50"
                aria-label="Next week"
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>

          <div className="ww-grid ww-grid-cols-7 ww-gap-2">
            {weekDates.map((date) => {
              const isSelected = isSameDay(date, selectedDate);
              const isToday = isSameDay(date, new Date());

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => setSelectedDate(date)}
                  className={`ww-py-3 ww-rounded-lg ww-text-center ww-transition-colors ${
                    isSelected
                      ? 'ww-bg-primary ww-text-white'
                      : isToday
                      ? 'ww-bg-gray-100'
                      : 'hover:ww-bg-gray-50'
                  }`}
                >
                  <div className={`ww-text-xs ww-font-medium ww-mb-1 ${
                    isSelected ? 'ww-text-white' : 'ww-text-gray-600'
                  }`}>
                    {format(date, 'EEE')}
                  </div>
                  <div className={`ww-text-lg ww-font-semibold ${
                    isSelected ? 'ww-text-white' : 'ww-text-gray-900'
                  }`}>
                    {format(date, 'd')}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slots */}
        <div>
          <h3 className="ww-font-medium ww-text-gray-900 ww-mb-4">Available times</h3>

          {selectedProvider ? (
            <div className="ww-space-y-4">
              {/* Morning */}
              {groupedSlots.morning.length > 0 && (
                <div>
                  <h4 className="ww-text-sm ww-font-medium ww-text-primary ww-mb-2">Morning</h4>
                  <div className="ww-flex ww-flex-wrap ww-gap-2">
                    {groupedSlots.morning.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot)}
                        className={`ww-px-3 ww-py-2 ww-text-sm ww-rounded ww-transition-colors ${
                          selectedSlot?.id === slot.id
                            ? 'ww-bg-primary ww-text-white'
                            : 'ww-bg-gray-100 ww-text-gray-700 hover:ww-bg-gray-200'
                        }`}
                      >
                        {format(new Date(slot.startTime), 'hh:mm a')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Afternoon */}
              {groupedSlots.afternoon.length > 0 && (
                <div>
                  <h4 className="ww-text-sm ww-font-medium ww-text-primary ww-mb-2">Afternoon</h4>
                  <div className="ww-flex ww-flex-wrap ww-gap-2">
                    {groupedSlots.afternoon.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot)}
                        className={`ww-px-3 ww-py-2 ww-text-sm ww-rounded ww-transition-colors ${
                          selectedSlot?.id === slot.id
                            ? 'ww-bg-primary ww-text-white'
                            : 'ww-bg-gray-100 ww-text-gray-700 hover:ww-bg-gray-200'
                        }`}
                      >
                        {format(new Date(slot.startTime), 'hh:mm a')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Evening */}
              {groupedSlots.evening.length > 0 && (
                <div>
                  <h4 className="ww-text-sm ww-font-medium ww-text-primary ww-mb-2">Evening</h4>
                  <div className="ww-flex ww-flex-wrap ww-gap-2">
                    {groupedSlots.evening.map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot)}
                        className={`ww-px-3 ww-py-2 ww-text-sm ww-rounded ww-transition-colors ${
                          selectedSlot?.id === slot.id
                            ? 'ww-bg-primary ww-text-white'
                            : 'ww-bg-gray-100 ww-text-gray-700 hover:ww-bg-gray-200'
                        }`}
                      >
                        {format(new Date(slot.startTime), 'hh:mm a')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading state */}
              {loadingSlots && (
                <p className="ww-text-sm ww-text-gray-500">
                  Loading available times...
                </p>
              )}

              {/* Error state */}
              {availabilityError && !loadingSlots && (
                <p className="ww-text-sm ww-text-red-500">
                  {availabilityError}
                </p>
              )}

              {/* No slots available */}
              {!loadingSlots && !availabilityError && convertedSlots.length === 0 && selectedService && selectedLocation && (
                <p className="ww-text-sm ww-text-gray-500">
                  No available times for this date. Please select another date.
                </p>
              )}

              {/* Missing selections */}
              {(!selectedService || !selectedLocation) && !loadingSlots && (
                <p className="ww-text-sm ww-text-gray-500">
                  Please select a service and location to see available times.
                </p>
              )}
            </div>
          ) : (
            <p className="ww-text-sm ww-text-gray-500">
              Please select a provider to see available times.
            </p>
          )}
        </div>
      </div>

      {/* Right Column - Patient Information */}
      <div className="lg:ww-col-span-2">
        <div className="ww-bg-white ww-border ww-border-gray-200 ww-rounded-lg ww-p-6 ww-sticky ww-top-36">
          <h2 className="ww-text-lg ww-font-semibold ww-text-gray-900 ww-mb-6">
            Enter your information
          </h2>

          <div className="ww-space-y-4">
            {/* Name and Phone */}
            <div className="ww-grid ww-grid-cols-2 ww-gap-4">
              <div>
                <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                  Name
                </label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  value={patientInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                />
              </div>
              <div>
                <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                  Phone number
                </label>
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  value={patientInfo.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                />
              </div>
            </div>

            {/* Email and DOB */}
            <div className="ww-grid ww-grid-cols-2 ww-gap-4">
              <div>
                <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={patientInfo.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                />
              </div>
              <div>
                <CustomDatePicker
                  date={dateOfBirthDate}
                  onDateChange={handleDateOfBirthChange}
                  label="Date of birth"
                  placeholder="Select date"
                  dateFormat="yyyy-MM-dd"
                  disablePastDates={false}
                  maxDate={startOfDay(new Date())}
                  showOkCancelButtons={true}
                  showClearButton={true}
                  testId="date-of-birth-input"
                />
              </div>
            </div>

            {/* Reason for Visit */}
            <div>
              <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                Reason for visit
              </label>
              <textarea
                placeholder=""
                value={patientInfo.reasonForVisit}
                onChange={(e) => handleInputChange('reasonForVisit', e.target.value)}
                rows={4}
                className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary ww-resize-none"
              />
            </div>

            {/* Payment Method */}
            {paymentMethods && paymentMethods.length > 0 && (
              <div>
                <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                  Payment method{' '}
                  <span className="ww-text-gray-400 ww-font-normal">(Optional)</span>
                </label>
                <select
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                >
                  <option value="">Select payment method</option>
                  {paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Insurance Toggle */}
            {!showInsurance ? (
              <button
                onClick={() => setShowInsurance(true)}
                className="ww-inline-flex ww-items-center ww-gap-2 ww-text-sm ww-text-primary ww-font-medium ww-border ww-border-primary ww-rounded-lg ww-px-4 ww-py-2 hover:ww-bg-primary/5 ww-transition-colors"
              >
                <PlusIcon />
                Add insurance information
              </button>
            ) : (
              <div className="ww-space-y-4 ww-pt-4 ww-border-t">
                <h3 className="ww-text-sm ww-font-medium ww-text-gray-900">
                  Enter your insurance information{' '}
                  <span className="ww-text-gray-400 ww-font-normal">(Optional)</span>
                </h3>

                <div>
                  <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                    Insurance provider
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your insurance provider"
                    value={patientInfo.insuranceProvider}
                    onChange={(e) => handleInputChange('insuranceProvider', e.target.value)}
                    className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                  />
                </div>

                <div className="ww-grid ww-grid-cols-2 ww-gap-4">
                  <div>
                    <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                      Member ID
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your member ID"
                      value={patientInfo.memberId}
                      onChange={(e) => handleInputChange('memberId', e.target.value)}
                      className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                    />
                  </div>
                  <div>
                    <label className="ww-block ww-text-sm ww-font-medium ww-text-gray-700 ww-mb-1">
                      Group Member
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your group member"
                      value={patientInfo.groupMember}
                      onChange={(e) => handleInputChange('groupMember', e.target.value)}
                      className="ww-w-full ww-px-3 ww-py-2.5 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm focus:ww-outline-none focus:ww-ring-2 focus:ww-ring-primary/20 focus:ww-border-primary"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Review Booking Button */}
          <div className="ww-mt-6">
            <button
              onClick={handleReviewBooking}
              disabled={!isFormComplete}
              className={`ww-w-full ww-py-3 ww-rounded-lg ww-font-medium ww-text-sm ww-transition-colors ${
                isFormComplete
                  ? 'ww-bg-primary ww-text-white hover:ww-bg-primary/90'
                  : 'ww-bg-gray-100 ww-text-gray-400 ww-cursor-not-allowed'
              }`}
            >
              Review booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
